import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import { CoworkTask } from '../../types/cowork';
import {
  ActionBar,
  Badge,
  Button,
  Input,
  Muted,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Table,
  TableWrap,
  TabBar,
  TabBtn,
  Toolbar,
} from '../ui';

type Filter = 'all' | 'focused' | 'pending' | 'completed';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'focused', label: 'Con focus' },
  { id: 'pending', label: 'Pendientes' },
  { id: 'completed', label: 'Completadas' },
];

function formatTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires',
  });
}

function workedFor(task: CoworkTask) {
  if (!task.createdAt) return '—';
  const end = task.completedAt ? new Date(task.completedAt).getTime() : Date.now();
  const minutes = Math.max(0, Math.round((end - new Date(task.createdAt).getTime()) / 60000));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

const CoworkCommunityTab: React.FC<{ refresh: () => Promise<void> }> = ({ refresh }) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [tasks, setTasks] = useState<CoworkTask[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<CoworkTask[]>('/cowork/community', {
        params: { filter, search },
      });
      setTasks(data);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const id = window.setTimeout(load, search ? 300 : 0);
    return () => window.clearTimeout(id);
  }, [load, search]);

  const moderate = async (taskId: string, action: 'archive' | 'delete' | 'complete') => {
    await api.post(`/cowork/community/${taskId}/moderate`, { action });
    await Promise.all([load(), refresh()]);
  };

  return (
    <Surface>
      <SurfaceHeader>
        <SurfaceTitle>Tareas del chat</SurfaceTitle>
        <Toolbar>
          <TabBar>
            {FILTERS.map((f) => (
              <TabBtn key={f.id} $active={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.label}
              </TabBtn>
            ))}
          </TabBar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar usuario"
            style={{ width: 200 }}
          />
          <Button $variant="ghost" $size="sm" onClick={load}>Actualizar</Button>
        </Toolbar>
      </SurfaceHeader>

      <SurfaceBody $flush>
        {loading && <div style={{ padding: '1rem' }}><Muted>Cargando…</Muted></div>}
        {!loading && tasks.length === 0 && (
          <div style={{ padding: '1rem' }}>
            <Muted>No hay tareas para este filtro.</Muted>
          </div>
        )}
        {!loading && tasks.length > 0 && (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th title="El número que el viewer usa en !listo y !deshacer">Nº</th>
                  <th>Tarea</th>
                  <th>Estado</th>
                  <th>Creada</th>
                  <th>Completada</th>
                  <th>Tiempo</th>
                  <th style={{ textAlign: 'right' }}>Moderar</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td style={{ color: '#fff', fontWeight: 500 }}>{task.displayName || '—'}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{task.number || '—'}</td>
                    <td style={{ color: '#fff' }}>
                      {task.focused && <span title="En foco">🎯 </span>}
                      {task.text}
                    </td>
                    <td>
                      <Badge $active={task.status === 'completed'}>
                        {task.status === 'completed' ? 'Completada' : task.focused ? 'En foco' : 'Pendiente'}
                      </Badge>
                    </td>
                    <td>{formatTime(task.createdAt)}</td>
                    <td>{formatTime(task.completedAt)}</td>
                    <td>{workedFor(task)}</td>
                    <td>
                      <ActionBar>
                        {task.status !== 'completed' && (
                          <Button $variant="ghost" $size="sm" onClick={() => moderate(task.id, 'complete')}>
                            Completar
                          </Button>
                        )}
                        <Button $variant="ghost" $size="sm" onClick={() => moderate(task.id, 'archive')}>
                          Ocultar
                        </Button>
                        <Button $variant="danger" $size="sm" onClick={() => moderate(task.id, 'delete')}>
                          Borrar
                        </Button>
                      </ActionBar>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </SurfaceBody>
    </Surface>
  );
};

export default CoworkCommunityTab;
